import classNames from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppAuthState } from "../../hooks/useAppAuthState";
import { useGetUserProfiles } from "../../hooks/useGetUserProfiles";
import { usePlaceCheckInStatus } from "../../hooks/usePlaceCheckInStatus";
import { usePlaceInfo } from "../../hooks/usePlaceInfo";
import { PlaceholderImage } from "../../pages/ProfilePage/ProfileImageUpdate";
import { checkIn, checkOut } from "../../services/checkInService";
import ErrorDisplay from "../ErrorDisplay/ErrorDisplay";
import { Headings } from "../Headings";
import "./CheckInBox.scss";

interface CheckInBoxProps {
  className?: string;
  placeId?: string;
}

const CheckInBox: React.FC<CheckInBoxProps> = ({ placeId, className }) => {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAppAuthState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigateToSignIn = useCallback(() => {
    navigate("/login?redirect=" + encodeURIComponent(window.location.pathname));
  }, [navigate]);

  const {
    place,
    loading: placeLoading,
    error: placeError,
    refetch: refetchPlaceInfo,
  } = usePlaceInfo(placeId || "");

  const {
    isLoading: isCheckInStatusLoading,
    isCheckedInAtThisPlace,
    currentCheckInEventId,
    isCheckedInElsewhere,
    checkedInPlaceName,
  } = usePlaceCheckInStatus(placeId, user?.uid);

  const handleCheckIn = useCallback(async () => {
    if (!user || !placeId || !place?.name) {
      return;
    }

    setIsSubmitting(true);
    try {
      await checkIn(user.uid, placeId, place.name);
      refetchPlaceInfo();
      toast.success("Checked in to " + place.name);
    } catch (err) {
      toast.error("Check-in failed");
    } finally {
      setIsSubmitting(false);
    }
  }, [user, placeId, place, refetchPlaceInfo]);

  const handleCheckOut = useCallback(async () => {
    if (!user || !placeId || !currentCheckInEventId || !place?.name) {
      return;
    }

    setIsSubmitting(true);
    try {
      // For checkout, we use the placeId from the current page context,
      // as the user is checking out from *this* place.
      await checkOut(user.uid, placeId, currentCheckInEventId);
      refetchPlaceInfo();
      toast.success("Checked out from " + place.name);
    } catch (err) {
      toast.error("Check-out failed");
    } finally {
      setIsSubmitting(false);
    }
  }, [user, placeId, currentCheckInEventId, refetchPlaceInfo, place?.name]);

  const { checkedInUserIds, checkInsInLast30Days } = useMemo(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    let count = 0;
    if (place?.checkInHistory) {
      for (const userId in place.checkInHistory) {
        const checkInTime = place.checkInHistory[userId].toDate();
        if (checkInTime >= thirtyDaysAgo) {
          count++;
        }
      }
    }

    return {
      checkedInUserIds: place?.checkedInUserIds ?? [],
      checkInsInLast30Days: count,
    };
  }, [place]);

  const {
    profiles: checkedInUserProfiles,
    loading: checkedInUserProfilesLoading,
  } = useGetUserProfiles(checkedInUserIds, !user?.uid);

  const dataLoadedContent = useMemo(() => {
    if (
      !place ||
      placeLoading ||
      isCheckInStatusLoading ||
      checkedInUserProfilesLoading
    ) {
      return (
        <div className="check-in-box-content">
          <div className="loading-message alert alert-info">
            Loading check-in status...
          </div>
        </div>
      );
    }

    let checkInActionButton = null;
    if (!user) {
      checkInActionButton = (
        <button className="btn btn-primary" onClick={navigateToSignIn}>
          Sign in to check-in
        </button>
      );
    } else if (isCheckedInAtThisPlace) {
      checkInActionButton = (
        <button
          className="btn btn-primary"
          onClick={handleCheckOut}
          disabled={isSubmitting || !place}
        >
          Check-out from {place.name}
        </button>
      );
    } else {
      checkInActionButton = (
        <div>
          <button
            className="btn btn-primary"
            onClick={handleCheckIn}
            disabled={isSubmitting || !place}
          >
            Check-in to {place.name}
          </button>
          {isCheckedInElsewhere && checkedInPlaceName && (
            <div className="checkin-caption">
              Checking into {place.name} will check you out of{" "}
              <span className="checked-in-place-name">
                {checkedInPlaceName}
              </span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="check-in-box-content">
        {!isLoggedIn && (
          <div className="alert alert-warning">
            {checkedInUserProfiles.length === 0
              ? "Sign in to see who is checked in."
              : checkedInUserProfiles.length === 1
                ? `Sign in to see the one lonely person checked in.`
                : `Sign in to see the ${checkedInUserProfiles.length} people checked in.`}
          </div>
        )}
        {isLoggedIn && checkedInUserProfiles.length === 0 && (
          <div className="no-one-checked-in alert alert-warning">
            No one is checked in @ {place.name}
          </div>
        )}
        {checkedInUserProfiles.length > 0 && (
          <div className="checked-in-users">
            {checkedInUserProfiles.map((profile) => (
              <div key={profile.userId}>
                <img
                  src={profile.photoURL || PlaceholderImage}
                  alt={profile.username}
                  className="profile-mini"
                />
              </div>
            ))}
          </div>
        )}
        <div className="check-in-actions">{checkInActionButton}</div>
        {checkInsInLast30Days > 0 && (
          <div className="check-in-caption caption-cta">
            {checkInsInLast30Days} check-in
            {checkInsInLast30Days !== 1 ? "s" : ""} in the last 30 days.
          </div>
        )}
      </div>
    );
  }, [
    place,
    placeLoading,
    isCheckInStatusLoading,
    checkedInUserProfilesLoading,
    user,
    isCheckedInAtThisPlace,
    isLoggedIn,
    checkedInUserProfiles,
    navigateToSignIn,
    handleCheckOut,
    isSubmitting,
    handleCheckIn,
    isCheckedInElsewhere,
    checkedInPlaceName,
    checkInsInLast30Days,
  ]);

  const checkInBoxClassName = classNames("check-in-box", className);

  return (
    <div className={checkInBoxClassName}>
      <Headings.PhoneSubBox>Check-ins</Headings.PhoneSubBox>
      {placeError && <ErrorDisplay error={placeError} />}
      {dataLoadedContent}
    </div>
  );
};

export default CheckInBox;
